import React from "react";
import { useField, useFormikContext } from "formik";
import { TextFieldConfig } from "../types";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import Input from "../../core/input";
import ErrorField from "../errorField";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "../../util";
import FormOverwriteField from "../overwriteField";
import { isEmpty } from "../../../utils";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { useFormContextData } from "shared/form/context";
import { InputType } from "shared/form/enum";

function FormTextField({ doNotRunOnBlurRef, shouldSkipOnBlur, onBlur: propsBlurHandler, ...props }: TextFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange } = useFormikContext();
  const [{ value, onBlur }, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  const changeHandler = React.useCallback(
    async e => {
      try {
        await setFieldValue(props.name, e.target.value, false);
        if (!meta.touched) {
          await setFieldTouched(props.name, true, false);
        }
        validateOnChange && (await validateForm());
        props.onChange?.(e);
      } catch (e) {}
    },
    [setFieldValue, props, meta.touched, validateOnChange, validateForm, setFieldTouched]
  );

  const blurHandler = React.useCallback(
    async e => {
      propsBlurHandler?.(e);
      if (doNotRunOnBlurRef?.current || shouldSkipOnBlur?.()) return;
      await setFieldValue(props.name, e.target.value?.trim());
      await onBlur(e);
    },
    [doNotRunOnBlurRef, shouldSkipOnBlur, props.name, setFieldValue, onBlur, propsBlurHandler]
  );

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
        <FormLabel
          htmlFor={props.name}
          label={props.label}
          required={props.required}
          additionalLabel={props.additionalLabel}
          infoLabel={props.infoLabel}
        />
        {formContextData.isReadOnly ? (
          getValueForReadOnlyFieldForStaticForm({
            renderType:
              props.type === InputType.PASSWORD ? FieldRenderType.PASSWORD : FieldRenderType.SINGLE_LINE_TEXTFIELD,
            value
          })
        ) : (
          <Input
            name={props.name}
            value={value}
            onBlur={blurHandler}
            onChange={changeHandler}
            type={props.type}
            disabled={props.disabled}
            className={props.className}
            placeholder={props.placeholder}
            icon={props.icon}
            hasError={meta.touched && meta.error}
            autoComplete={props.autoComplete}
            autoFocus={props.autoFocus}
            switchIconPosition={props.switchIconPosition}
            onFocus={props.onFocus}
            onKeyDown={props.onKeyDown}
            disablePaste={props.disablePaste}
          />
        )}

        <ErrorField meta={meta} />
        {!isEmpty(props.conflictValue) && <FormOverwriteField forFieldName={props.name} disabled={props.disabled} />}
        {props.enableFieldReset && (
          <FieldResetWhenRemoved name={props.name} fixedInitialValueOnReset={props.fixedInitialValueOnReset} />
        )}
      </FormGroup>
    </React.Fragment>
  );
}

export default FormTextField;
